import { observer } from 'src/utils/mobx-react';
import React, { useState } from 'react';
import { MarketSettings } from 'src/domains/sportsbook/trading/view/eventDetails/markets/marketsSettings/MarketSettings';
import { SortableMarketSelections } from 'src/domains/sportsbook/trading/view/eventDetails/markets/selectionsSettings/SortableMarketSelections';
import { MarketTags } from 'src/domains/sportsbook/trading/view/eventDetails/markets/marketsSettings/marketTags/MarketTags';
import Loading from 'src/components/Loading';
import { useAppStateContext } from 'src/AppState/AppState';
import { MarketSettingsFormState } from 'src/domains/sportsbook/trading/view/eventDetails/markets/MarketSettingsForm.state';
import {
    SelectionHeaderCell,
    SelectionHeaderRow,
    SelectionMargin,
} from 'src/domains/sportsbook/trading/view/eventDetails/markets/MarketSettingsForm.style';
import { MarketId } from 'src_common/common/websocket2/id/WebsocketId';
import { AutoMapSerialized } from 'src_common/common/mobx-utils/AutoMap';

const SELECTIONS_ORDERS = [
    {
        id: '-',
        label: 'Display/Creation',
    },
    {
        id: 'by-active-display',
        label: 'Active & Display',
    },
    {
        id: 'by-price',
        label: 'Price ASC',
    },
    {
        id: 'reverse-by-price',
        label: 'Price DESC',
    },
    {
        id: 'by-selection-name',
        label: 'Name ASC',
    },
    {
        id: 'reverse-by-selection-name',
        label: 'Name DESC',
    },
];

export interface MarketSettingsFormPropsType {
    marketId: MarketId | null;
    onAddSelection: (marketId: number) => void;
    onAddAllSelections: (eventId: number, marketId: number, participants?: unknown) => void;
}

export const MarketSettingsForm = observer('MarketSettingsForm', (props: MarketSettingsFormPropsType) => {
    const appState = useAppStateContext();
    const [stateMap] = useState(
        () =>
            new AutoMapSerialized<MarketId | null, MarketSettingsFormState>(
                (marketId: MarketId | null) => marketId?.rawId ?? '',
                (marketId: MarketId | null) => new MarketSettingsFormState(appState, marketId)
            )
    );
    const { marketId } = props;

    const state = stateMap.get(marketId);

    if (marketId === null) {
        return null;
    }

    const marketModel = marketId.getModel();

    if (marketModel === null) {
        return (
            <div className='market-settings-form'>
                <Loading
                    active={true}
                    // label='Adding selections...'
                />
            </div>
        );
    }

    const allowAddParticipantButton =
        marketModel.eventModel?.eventType === 'multi-participants-event' ||
        marketModel.eventModel?.eventType === 'multi-participant' ||
        marketModel.eventModel?.eventType === 'multi-participant-ante-post' ||
        marketModel.template.name.toLowerCase() === 'custom' ||
        marketModel.eventModel?.eventType === 'outrights';
    const allowAddBlankParticipantButtons =
        allowAddParticipantButton || marketModel.template.marketTemplateType?.toLowerCase() === 'standard';

    const onAddSelectionHandler = (): void => {
        if (props.marketId !== null) {
            props.onAddSelection(props.marketId.toOldId());
        }
    };

    const onAddAllSelectionsHandler = (): void => {
        const { onAddAllSelections } = props;

        const marketModel = marketId.getModel();
        if (marketModel === null) {
            return;
        }
        onAddAllSelections(marketModel.eventId, marketId.toOldId());
    };

    return (
        <div className='market-settings-form'>
            <div className='row'>
                <MarketSettings marketId={marketId} />
                <div className='col-6 selections-settings'>
                    <h4>
                        {allowAddBlankParticipantButtons ? (
                            <button
                                type='button'
                                className='btn btn-add'
                                onClick={onAddSelectionHandler}
                            >
                                Add Blank
                            </button>
                        ) : null}
                        {allowAddParticipantButton ? (
                            <button
                                type='button'
                                className='btn btn-add'
                                onClick={onAddAllSelectionsHandler}
                            >
                                Add Participant
                            </button>
                        ) : null}
                        Selections
                    </h4>
                    <ul className='selections-settings-form'>
                        <li className='selections-settings-form__precentages selections-settings-form-order'>
                            <SelectionHeaderRow>
                                <SelectionHeaderCell>
                                    <div className='element'>
                                        <div className='select-wrapper select-sort'>
                                            <select
                                                onChange={state.onSortOrderChange}
                                                data-test='selection-sort-select'
                                                defaultValue={state.sortOrder}
                                            >
                                                <option />
                                                {SELECTIONS_ORDERS.map((orderItem) => {
                                                    return (
                                                        <option
                                                            key={orderItem.id}
                                                            value={orderItem.id}
                                                        >
                                                            {orderItem.label}
                                                        </option>
                                                    );
                                                })}
                                            </select>
                                        </div>
                                    </div>
                                </SelectionHeaderCell>
                                <SelectionHeaderCell>
                                    <SelectionMargin selectionsMargin={state.margin}>
                                        {`${state.margin}% `}
                                    </SelectionMargin>
                                    {state.placeMargin > 0 ? (
                                        <SelectionMargin selectionsMargin={state.placeMargin}>
                                            {`(${state.placeMargin}%)`}
                                        </SelectionMargin>
                                    ) : null}
                                </SelectionHeaderCell>
                                <div className='flex text-left'>
                                    <SelectionHeaderCell>
                                        <div
                                            className='element checkbox'
                                            data-test='display-all'
                                        >
                                            <label>
                                                <input
                                                    type='checkbox'
                                                    checked={state.isAnySelectionDisplay}
                                                    onClick={state.onDisplayAllChange}
                                                />
                                                <span>Display All</span>
                                            </label>
                                        </div>
                                    </SelectionHeaderCell>
                                </div>
                                <div className='flex text-left'>
                                    <SelectionHeaderCell>
                                        <div
                                            className='element checkbox'
                                            data-test='active-all'
                                        >
                                            <label>
                                                <input
                                                    type='checkbox'
                                                    checked={state.isAnySelectionActive}
                                                    onClick={state.onActiveAllChange}
                                                />
                                                <span>Active All</span>
                                            </label>
                                        </div>
                                    </SelectionHeaderCell>
                                </div>
                                <SelectionHeaderCell />
                            </SelectionHeaderRow>
                        </li>
                        <SortableMarketSelections
                            marketId={marketModel.id}
                            order={state.sortOrder}
                        />
                    </ul>
                </div>
                <MarketTags
                    marketId={marketId}
                    onSetSelectionOrder={state.setSelectionOrder}
                />
            </div>
        </div>
    );
});
